import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { ChevronRight, Check, ChevronLeft, GraduationCap, Target, Globe, Lightbulb, BookOpen } from "lucide-react";
import { toast } from "sonner";

const TESTS = [
  { id: "IELTS_ACADEMIC", name: "IELTS Academic", desc: "For higher education abroad." },
  { id: "IELTS_GENERAL", name: "IELTS General", desc: "For immigration and work." },
  { id: "PTE", name: "PTE Academic", desc: "Computer-based, AI-scored." },
  { id: "OXFORD_ELLT", name: "Oxford ELLT", desc: "Oxford-style proficiency." },
  { id: "DUOLINGO", name: "Duolingo English", desc: "On-demand, online." },
];
const LEVELS = [
  { id: "BEGINNER", label: "Beginner", desc: "Just starting out (CEFR A1–A2)" },
  { id: "INTERMEDIATE", label: "Intermediate", desc: "Comfortable conversational (B1–B2)" },
  { id: "ADVANCED", label: "Advanced", desc: "Fluent with depth (C1–C2)" },
];
const MODES = [
  { id: "SELF", label: "AI Self-Practice", desc: "Subscription, AI evaluation only" },
  { id: "HYBRID", label: "Hybrid Live", desc: "AI + live online classes with a mentor" },
];

export default function Onboarding() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ test_type: "", target_score: "", current_level: "", country: "", learning_mode: "" });
  const [loading, setLoading] = useState(false);

  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    setLoading(true);
    try {
      const r = await api.post("/auth/onboarding", data);
      setUser(r.data);
      toast.success("All set — let's go!");
      nav("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setLoading(false); }
  };

  const steps = [
    {
      icon: GraduationCap,
      title: "Choose your exam",
      sub: "We'll tailor every drill, mock and feedback to this test.",
      canNext: !!data.test_type,
      content: (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {TESTS.map((t) => (
            <button key={t.id} data-testid={`onb-test-${t.id}`} onClick={() => setData({ ...data, test_type: t.id })}
              className={`text-left px-5 py-4 rounded-2xl border transition-all ${
                data.test_type === t.id ? "border-[#F77F00] bg-[#FFF4E5]" : "border-slate-200 hover:border-[#003049]/40"
              }`}>
              <div className="font-display text-base text-[#003049]">{t.name}</div>
              <div className="text-xs text-slate-500 mt-1">{t.desc}</div>
            </button>
          ))}
        </div>
      ),
    },
    {
      icon: Target,
      title: "What's your target score?",
      sub: "We'll build a plan to reach it on time.",
      canNext: !!data.target_score,
      content: (
        <input data-testid="onb-target-input" value={data.target_score} onChange={(e) => setData({ ...data, target_score: e.target.value })}
          placeholder="e.g. Band 7.5 · Score 79 · Score 145"
          className="input-base text-lg" />
      ),
    },
    {
      icon: Lightbulb,
      title: "Your current level",
      sub: "Honest is best — we'll calibrate the difficulty.",
      canNext: !!data.current_level,
      content: (
        <div className="space-y-2">
          {LEVELS.map((l) => (
            <button key={l.id} data-testid={`onb-level-${l.id}`} onClick={() => setData({ ...data, current_level: l.id })}
              className={`w-full text-left px-5 py-4 rounded-2xl border transition-all ${
                data.current_level === l.id ? "border-[#F77F00] bg-[#FFF4E5]" : "border-slate-200 hover:border-[#003049]/40"
              }`}>
              <div className="font-display text-base text-[#003049]">{l.label}</div>
              <div className="text-xs text-slate-500 mt-1">{l.desc}</div>
            </button>
          ))}
        </div>
      ),
    },
    {
      icon: Globe,
      title: "Where are you headed?",
      sub: "Helps us recommend universities & visa specifics.",
      canNext: true,
      content: (
        <input data-testid="onb-country-input" value={data.country} onChange={(e) => setData({ ...data, country: e.target.value })}
          placeholder="University or country (e.g. University of Oxford / Canada)"
          className="input-base text-lg" />
      ),
    },
    {
      icon: BookOpen,
      title: "How do you want to learn?",
      sub: "You can switch between modes anytime.",
      canNext: !!data.learning_mode,
      content: (
        <div className="grid sm:grid-cols-2 gap-3">
          {MODES.map((m) => (
            <button key={m.id} data-testid={`onb-mode-${m.id}`} onClick={() => setData({ ...data, learning_mode: m.id })}
              className={`text-left px-5 py-5 rounded-2xl border transition-all ${
                data.learning_mode === m.id ? "border-[#F77F00] bg-[#FFF4E5]" : "border-slate-200 hover:border-[#003049]/40"
              }`}>
              <div className="font-display text-base text-[#003049]">{m.label}</div>
              <div className="text-xs text-slate-500 mt-1">{m.desc}</div>
            </button>
          ))}
        </div>
      ),
    },
  ];

  const cur = steps[step];
  const isLast = step === steps.length - 1;
  const Icon = cur.icon;

  return (
    <div className="min-h-screen bg-[#F6F8FB] flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-[0_30px_80px_-30px_rgba(11,45,92,0.20)]">
          <div className="surface-navy text-white px-8 py-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center"><Icon className="w-5 h-5" /></div>
              <div>
                <div className="text-xs uppercase tracking-widest text-white/60">Step {step + 1} of {steps.length}</div>
                <div className="font-display text-xl mt-0.5">{cur.title}</div>
              </div>
            </div>
            <div className="text-xs text-white/60 font-mono">LINGOQ ONBOARDING</div>
          </div>
          <div className="px-8 pt-2 pb-1 flex gap-1.5 bg-white">
            {steps.map((_, i) => (
              <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= step ? "bg-[#F77F00]" : "bg-slate-100"}`} />
            ))}
          </div>
          <div className="p-8">
            <p className="text-slate-500 mb-6 text-sm">{cur.sub}</p>
            {cur.content}
            <div className="flex items-center justify-between mt-10">
              <button onClick={back} disabled={step === 0} data-testid="onb-back" className="px-5 py-2.5 text-sm text-slate-500 hover:text-[#003049] disabled:opacity-30 inline-flex items-center gap-1">
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
              {isLast ? (
                <button onClick={submit} disabled={!cur.canNext || loading} data-testid="onb-finish" className="btn-primary inline-flex items-center gap-2 disabled:opacity-40">
                  {loading ? "Saving..." : (<>Finish setup <Check className="w-4 h-4" /></>)}
                </button>
              ) : (
                <button onClick={next} disabled={!cur.canNext} data-testid="onb-next" className="btn-primary inline-flex items-center gap-2 disabled:opacity-40">
                  Continue <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
